import { defineStore } from 'pinia';
import { toRaw, ref, watch } from 'vue';
import router from '@/router';
import { socketService } from '../../../../components/socket/SocketService';
import { useDutyStore } from './useDutyStore';
import { usePlanningStore } from './usePlanningStore';
import { usePreviewStore } from './usePreviewStore';
import { useAlertStore } from './useAlertStore';
import { debounce } from 'lodash';
import { useUserStore } from './useUserStore';
import i18n from '@/plugins/i18n';
import { useDayStore } from './useDayStore';
import { CurrentDayDuty, DutyCalendar, DutyAvailabilityResponse } from '../types/api/duties-response';
import { CurrentDayDriver, UserAvailabilityResponse } from '../types/api/users-response';
import { ApiDuty } from '../types/api/duties-response';
import { DutyChangePreviewResponse } from '@/rennovation/planning-view/types/api/preview-response';

type DutyEventType = 'create' | 'update' | 'delete';

interface PendingDutyEvent {
	seq: number;
	type: DutyEventType;
	dutyId: string;
	data: Record<string, any>;
}

interface FoldedDutyEvent {
	type: DutyEventType;
	data: Record<string, any>; // raw socket payload for this duty, shape varies by event type
	seq: number;
}

interface DutyLocation {
	user: string;
	day: string;
}

interface UpdateAllSourcesCtx {
	nextMappedByDay: DutyCalendar;
	nextMappedById: Record<string, ApiDuty>;
	nextUnallocatedById: Record<string, ApiDuty>;
	nextDeletedDuties: Record<string, ApiDuty>;
	nextCurrentDayDutiesById: Record<string, CurrentDayDuty>;
	nextCurrentDayDriversById: Record<string, CurrentDayDriver>;
	nextCurrentDayDuties: DutyAvailabilityResponse | null;
	nextCurrentDayDrivers: UserAvailabilityResponse | null;
	dutyLocationIndex: Map<string, DutyLocation>;
}

export const useSocketStore = defineStore('socket', () => {
	const isConnected = ref(false);
	const dutyStore = useDutyStore();
	const planningStore = usePlanningStore();
	const dayStore = useDayStore();
	const previewStore = usePreviewStore();
	const alertStore = useAlertStore();
	const t = (key: string, params?: Record<string, unknown> | unknown[]) => {
		return i18n.global.t(key, params);
	};
	const activeViewContext = ref(router.currentRoute._value.path);

	watch(
		() => router.currentRoute.value.path,
		(newPath) => {
			activeViewContext.value = newPath;
		},
		{ immediate: true }
	);

	function bindSocket(socketInstance) {
		if (!socketInstance) return;

		const raw = toRaw(socketInstance);
		const socket = raw?.socket || raw?.getInstance?.() || raw;

		if (!socket || typeof socket.on !== 'function') {
			console.warn('[useSocketStore]: Provided object is not a valid socket instance');
			return;
		}

		isConnected.value = true;

		autoBindEvents(socket, [
			'updated_Duty',
			'deleted_Duty',
			'created_Duty',
			'loadStatistics',
			'updated_Day',
			'created_Day',
			'created_User',
			'updated_User',
			'deleted_User',
			'updated_UserGroup',
			'deleted_UserGroup',
			'updated_Preview',
			'created_Preview',
			'preview_alert_triggered',
		]);
	}

	async function initializeSocket({ tenant, token, isTenantChanged }) {
		if ((!socketService.isConnected() || isTenantChanged) && token && tenant) {
			try {
				const socket = await socketService.connect({
					tenant,
					token,
					component: 'app',
					forceReconnect: isTenantChanged,
				});

				if (socket) {
					bindSocket(socket);
				}
			} catch (socketError) {
				console.error('Failed to initialize socket connection:', socketError);
			}
		} else if (socketService.isConnected()) {
			bindSocket(socketService.getInstance());
		}
	}

	function autoBindEvents(socket, eventNames) {
		const socketActions = {
			socket_updated_Duty: socket_updated_Duty,
			socket_deleted_Duty: socket_deleted_Duty,
			socket_created_Duty: socket_created_Duty,
			socket_loadStatistics: socket_loadStatistics,
			socket_updated_Day: socket_updated_Day,
			socket_created_Day: socket_created_Day,
			socket_created_User: socket_created_User,
			socket_updated_User: socket_updated_User,
			socket_deleted_User: socket_deleted_User,
			socket_updated_UserGroup: socket_updated_UserGroup,
			socket_deleted_UserGroup: socket_deleted_UserGroup,
			socket_updated_Preview: socket_updated_Preview,
			socket_created_Preview: socket_created_Preview,
			socket_preview_alert_triggered: socket_preview_alert_triggered,
		};

		const ROUTE_AGNOSTIC_EVENTS = ['updated_Preview', 'created_Preview'];

		eventNames.forEach((eventName) => {
			socket.off(eventName);

			const targetActionName = `socket_${eventName}`;

			socket.on(eventName, (payload) => {
				const LIVE_UPDATE_ROUTES = ['/planning', '/solution'];

				if (!ROUTE_AGNOSTIC_EVENTS.includes(eventName) && !LIVE_UPDATE_ROUTES.includes(activeViewContext.value)) {
					return;
				}

				const handler = socketActions[targetActionName];

				if (typeof handler === 'function') {
					handler(payload);
				} else {
					console.warn(`No local handler function found for event: ${targetActionName}`);
				}
			});
		});
	}

	// ---------------------------------------------------------------------
	// Unified Duty Event Queue Pipeline
	// ---------------------------------------------------------------------
	const pendingDutyEvents: PendingDutyEvent[] = [];
	let globalDutySeq = 0;
	let dutyFlushTimeoutId = null;
	const DUTY_FLUSH_WINDOW_MS = 150;

	function enqueueDutyEvent(type, dutyId, data) {
		pendingDutyEvents.push({ seq: globalDutySeq++, type, dutyId, data });

		if (!dutyFlushTimeoutId) {
			dutyFlushTimeoutId = setTimeout(flushDutyEvents, DUTY_FLUSH_WINDOW_MS);
		}
	}

	const hasProperty = (obj: object, key: string) => Object.prototype.hasOwnProperty.call(obj, key);

	function isRestoreOperation(data: unknown): boolean {
		return !!data && hasProperty(data, 'deleted') && data.deleted === false;
	}

	async function socket_updated_Duty(payload) {
		const data = (payload as unknown)?.payload || payload;

		const dutyId = data?.id || data?._id;
		if (!data || !dutyId) return;

		const isMarkedDeleted = !!dutyStore.deletedDuties[dutyId];

		if (isMarkedDeleted) {
			// Always keep the deleted copy up to date
			dutyStore.$patch((state) => {
				state.deletedDuties[dutyId] = { ...state.deletedDuties[dutyId], ...data };
			});

			const stillInLiveSources =
				!!dutyStore.dutiesMappedById?.[dutyId] || !!dutyStore.unallocatedDutiesById?.[dutyId] || !!dutyStore.currentDayDutiesById?.[dutyId];

			// Restore, or clean up a stale live record. Otherwise ignore.
			if (isRestoreOperation(data) || stillInLiveSources) {
				enqueueDutyEvent('update', dutyId, data);
			}
			return;
		}

		enqueueDutyEvent('update', dutyId, data);
	}

	async function socket_created_Duty(payload) {
		const data = payload?.payload || payload;
		if (!data || (!data.id && !data._id)) return;
		const dutyId = data.id || data._id;

		if (dutyStore.deletedDuties[dutyId]) {
			delete dutyStore.deletedDuties[dutyId];
		}

		enqueueDutyEvent('create', dutyId, data);
	}

	async function socket_deleted_Duty(payload) {
		const data = payload?.payload || payload;
		if (!data || (!data.id && !data._id)) return;
		const dutyId = data.id || data._id;

		const lastKnown = dutyStore.dutiesMappedById?.[dutyId] || dutyStore.unallocatedDutiesById?.[dutyId] || dutyStore.currentDayDutiesById?.[dutyId] || {};

		dutyStore.$patch((state) => {
			state.deletedDuties[dutyId] = { ...lastKnown, ...state.deletedDuties[dutyId], ...data, deleted: true, user: null };
		});

		enqueueDutyEvent('delete', dutyId, data);
	}

	// Cached collator: constructing an Intl.Collator per comparison call is
	// noticeably slower than reusing one, and this comparator runs inside
	// sort() calls that can execute many times within a single flush.
	const entityCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

	const compareEntitiesNumerically = (a, b) => {
		const valA = String(a.name || a.title || a.code || a.id || a._id || '');
		const valB = String(b.name || b.title || b.code || b.id || b._id || '');
		return entityCollator.compare(valA, valB);
	};

	// Persistent duty-location index, kept across flushes.
	// Rebuilding this by scanning the whole grid (users x days) on every
	// flush is wasteful when sustained socket traffic triggers many flushes
	// back to back. We cache the index and rebuild only when
	// dutyStore.mappedByDayVersion changes.
	let cachedMappedByDayRaw: Record<string, any> | null = null;
	let cachedDutyLocationIndex: Map<string, { user: string; day: string }> | null = null;
	let cachedVersion: number | null = null;

	function getDutyLocationIndex(mappedByDay: DutyCalendar): Map<string, { user: string; day: string }> {
		const raw = toRaw(mappedByDay);

		const currentVersion = dutyStore.mappedByDayVersion;

		if (cachedVersion === currentVersion && cachedDutyLocationIndex) {
			return cachedDutyLocationIndex;
		}

		const locationIndex = new Map<string, { user: string; day: string }>();
		for (const userId of Object.keys(raw)) {
			const userDays = raw[userId];
			if (!userDays) continue;
			for (const dayKey of Object.keys(userDays)) {
				const cell = userDays[dayKey];
				const cellId = cell?.id || cell?._id;
				if (cellId) {
					locationIndex.set(cellId, { user: userId, day: dayKey });
				}
			}
		}

		cachedMappedByDayRaw = raw;
		cachedDutyLocationIndex = locationIndex;
		cachedVersion = currentVersion;
		return locationIndex;
	}

	function foldDutyEvents(flushedEvents: PendingDutyEvent[]): Map<string, FoldedDutyEvent> {
		const foldedEventsByDutyId = new Map<string, FoldedDutyEvent>();

		for (const dutyEvent of flushedEvents) {
			const existingFoldedEntry = foldedEventsByDutyId.get(dutyEvent.dutyId);

			if (dutyEvent.type === 'delete') {
				foldedEventsByDutyId.set(dutyEvent.dutyId, { type: 'delete', data: dutyEvent.data, seq: dutyEvent.seq });
			} else if (dutyEvent.type === 'create') {
				foldedEventsByDutyId.set(dutyEvent.dutyId, { type: 'create', data: dutyEvent.data, seq: dutyEvent.seq });
			} else if (existingFoldedEntry?.type === 'delete' && !isRestoreOperation(dutyEvent.data)) {
				foldedEventsByDutyId.set(dutyEvent.dutyId, {
					type: 'delete',
					data: { ...existingFoldedEntry.data, ...dutyEvent.data },
					seq: dutyEvent.seq,
				});
			} else if (existingFoldedEntry && (existingFoldedEntry.type === 'update' || existingFoldedEntry.type === 'create')) {
				foldedEventsByDutyId.set(dutyEvent.dutyId, {
					type: existingFoldedEntry.type,
					data: { ...existingFoldedEntry.data, ...dutyEvent.data },
					seq: dutyEvent.seq,
				});
			} else {
				foldedEventsByDutyId.set(dutyEvent.dutyId, { type: 'update', data: dutyEvent.data, seq: dutyEvent.seq });
			}
		}

		return foldedEventsByDutyId;
	}

	// ============================================================================
	// HELPER FUNCTIONS
	// ============================================================================

	/**
	 * Finds the current owner (user and day) for a duty from the location index.
	 */
	function findOwnerInMappedByDay(dutyLocationIndex, dutyId) {
		return dutyLocationIndex.get(dutyId) || { user: null, day: null };
	}

	/**
	 * Evicts a duty from its assigned user/day grid cell and location index.
	 */
	function evictDutyFromAllCells(ctx, dutyId) {
		const { dutyLocationIndex, nextMappedByDay } = ctx;
		const loc = dutyLocationIndex.get(dutyId);
		if (!loc || !loc.user || !loc.day) return;

		const userDays = nextMappedByDay[loc.user];
		if (userDays && (userDays[loc.day]?.id === dutyId || userDays[loc.day]?._id === dutyId)) {
			nextMappedByDay[loc.user] = { ...userDays, [loc.day]: null };
		}
		dutyLocationIndex.delete(dutyId);
	}

	/**
	 * Unassigns a driver and moves them into the unallocated drivers pool.
	 */
	function updateDriverUnassignment(ctx, driverId) {
		const { nextCurrentDayDrivers, nextCurrentDayDriversById } = ctx;
		if (!driverId || !nextCurrentDayDrivers?.data) return;

		let allDrivers = [...(nextCurrentDayDrivers.data.all || [])];
		let unallocatedDrivers = [...(nextCurrentDayDrivers.data.unallocated || [])];

		const driverIndex = allDrivers.findIndex((d) => d._id === driverId || d.id === driverId);

		if (driverIndex !== -1) {
			let unassignedDriver = {
				...allDrivers[driverIndex],
				duty: null,
				dutyId: null,
			};

			allDrivers[driverIndex] = unassignedDriver;
			nextCurrentDayDriversById[driverId] = unassignedDriver;

			const unallocatedIndex = unallocatedDrivers.findIndex((d) => d._id === driverId || d.id === driverId);
			if (unallocatedIndex !== -1) {
				unallocatedDrivers[unallocatedIndex] = unassignedDriver;
			} else {
				unallocatedDrivers.push(unassignedDriver);
			}

			unallocatedDrivers.sort(compareEntitiesNumerically);
			nextCurrentDayDrivers.data.all = allDrivers;
			nextCurrentDayDrivers.data.unallocated = unallocatedDrivers;
		}
	}

	/**
	 * Assigns a duty to a driver and updates unallocated driver status.
	 */
	function updateDriverAssignment(ctx, driverId, assignedDuty) {
		const { nextCurrentDayDrivers, nextCurrentDayDriversById } = ctx;
		if (!driverId || !assignedDuty || !nextCurrentDayDrivers?.data) return;

		let allDrivers = [...(nextCurrentDayDrivers.data.all || [])];
		let unallocatedDrivers = [...(nextCurrentDayDrivers.data.unallocated || [])];

		const driverIndex = allDrivers.findIndex((d) => d._id === driverId || d.id === driverId);

		if (driverIndex !== -1) {
			let assignedDriver = {
				...allDrivers[driverIndex],
				duty: assignedDuty,
				dutyId: assignedDuty.id || assignedDuty._id,
			};

			allDrivers[driverIndex] = assignedDriver;
			nextCurrentDayDriversById[driverId] = assignedDriver;

			const unallocatedIndex = unallocatedDrivers.findIndex((d) => d._id === driverId || d.id === driverId);
			if (unallocatedIndex !== -1) {
				unallocatedDrivers.splice(unallocatedIndex, 1);
			}

			nextCurrentDayDrivers.data.all = allDrivers;
			nextCurrentDayDrivers.data.unallocated = unallocatedDrivers;
		}
	}

	/**
	 * Removes all references to a deleted or stale duty across dictionaries and lists.
	 */
	function purgeStaleDutyReferences(ctx, dutyId) {
		const { nextMappedById, nextUnallocatedById, nextCurrentDayDutiesById, nextCurrentDayDuties } = ctx;

		evictDutyFromAllCells(ctx, dutyId);

		delete nextMappedById[dutyId];
		delete nextUnallocatedById[dutyId];
		delete nextCurrentDayDutiesById[dutyId];

		if (nextCurrentDayDuties?.data) {
			if (Array.isArray(nextCurrentDayDuties.data.all)) {
				nextCurrentDayDuties.data.all = nextCurrentDayDuties.data.all.filter((d) => d._id !== dutyId && d.id !== dutyId);
			}
			if (Array.isArray(nextCurrentDayDuties.data.unallocated)) {
				nextCurrentDayDuties.data.unallocated = nextCurrentDayDuties.data.unallocated.filter((d) => d._id !== dutyId && d.id !== dutyId);
			}
		}
	}

	/**
	 * Main dispatcher to update all stores, mappings, and list structures for a duty event.
	 */
	function updateAllSourcesWithDuty(ctx, dutyId, updatedDuty, originalUser, originalDay) {
		const { nextMappedByDay, nextMappedById, nextUnallocatedById, nextCurrentDayDutiesById, nextCurrentDayDuties, dutyLocationIndex } = ctx;

		if (updatedDuty) {
			updatedDuty = { ...updatedDuty, id: dutyId, _id: dutyId };
		}

		if (originalUser && (!updatedDuty || !updatedDuty.user || originalUser !== updatedDuty.user)) {
			updateDriverUnassignment(ctx, originalUser);
		}

		// 1. HARD DELETE / REMOVAL
		if (!updatedDuty) {
			purgeStaleDutyReferences(ctx, dutyId);
			return;
		}

		// 2. STRICT POOL PARTITIONING
		if (updatedDuty.user) {
			// ASSIGNED TO DRIVER CELL
			nextMappedById[dutyId] = updatedDuty;

			// ABSOLUTE EVICTION: Remove completely from unallocated dictionary
			delete nextUnallocatedById[dutyId];

			updateDriverAssignment(ctx, updatedDuty.user, updatedDuty);
		} else {
			// UNASSIGNED TO UNALLOCATED POOL
			nextUnallocatedById[dutyId] = updatedDuty;

			// ABSOLUTE EVICTION: Evict from mapped cells & mapped ID dictionary
			delete nextMappedById[dutyId];
			evictDutyFromAllCells(ctx, dutyId);
		}

		nextCurrentDayDutiesById[dutyId] = updatedDuty;

		// 3. SYNCHRONIZE LIST ARRAYS
		if (nextCurrentDayDuties?.data) {
			let allDuties = [...(nextCurrentDayDuties.data.all || [])];
			let unallocatedDuties = [...(nextCurrentDayDuties.data.unallocated || [])];

			const allIndex = allDuties.findIndex((duty) => duty._id === dutyId || duty.id === dutyId);
			const unallocatedIndex = unallocatedDuties.findIndex((duty) => duty._id === dutyId || duty.id === dutyId);

			let updatedDutyPayload = {
				...(allIndex !== -1 ? allDuties[allIndex] : {}),
				...updatedDuty,
				_id: dutyId,
				id: dutyId,
			};

			if (allIndex !== -1) {
				allDuties[allIndex] = updatedDutyPayload;
			} else {
				allDuties.push(updatedDutyPayload);
			}

			if (!updatedDuty.user) {
				if (unallocatedIndex !== -1) {
					unallocatedDuties[unallocatedIndex] = updatedDutyPayload;
				} else {
					unallocatedDuties.push(updatedDutyPayload);
				}
			} else if (unallocatedIndex !== -1) {
				unallocatedDuties.splice(unallocatedIndex, 1);
			}

			unallocatedDuties.sort(compareEntitiesNumerically);
			nextCurrentDayDuties.data.all = allDuties;
			nextCurrentDayDuties.data.unallocated = unallocatedDuties;
		}

		// Clear old user/day cell mapping if reassigned to another driver or day
		if (originalUser && (originalUser !== updatedDuty.user || originalDay !== updatedDuty.day) && nextMappedByDay[originalUser]) {
			if (nextMappedByDay[originalUser][originalDay]?.id === dutyId || nextMappedByDay[originalUser][originalDay]?._id === dutyId) {
				nextMappedByDay[originalUser] = { ...nextMappedByDay[originalUser] };
				nextMappedByDay[originalUser][originalDay] = null;
			}
			dutyLocationIndex.delete(dutyId);
		}

		// Set destination cell mapping
		if (updatedDuty.user && updatedDuty.day) {
			if (!nextMappedByDay[updatedDuty.user]) {
				nextMappedByDay[updatedDuty.user] = {};
			}
			nextMappedByDay[updatedDuty.user] = {
				...nextMappedByDay[updatedDuty.user],
				[updatedDuty.day]: updatedDuty,
			};
			// Keep the reverse index in sync within this flush so later
			// events in the same batch resolve the duty's owner correctly.
			dutyLocationIndex.set(dutyId, { user: updatedDuty.user, day: updatedDuty.day });
		}
	}

	// ============================================================================
	// MAIN FLUSH METHOD
	// ============================================================================

	function flushDutyEvents() {
		if (pendingDutyEvents.length === 0) return;

		const events = pendingDutyEvents.splice(0);
		dutyFlushTimeoutId = null;

		const dutyEventMap = foldDutyEvents(events);
		const targetsToProcess = [...dutyEventMap.keys()];
		const dutyEntries = [...dutyEventMap.entries()];

		const userStore = useUserStore();

		// Context object grouping all working copies to pass clean state references to extracted functions
		const ctx: UpdateAllSourcesCtx = {
			nextMappedByDay: { ...(dutyStore.dutiesMappedByDay || {}) },
			nextMappedById: { ...(dutyStore.dutiesMappedById || {}) },
			nextUnallocatedById: { ...(dutyStore.unallocatedDutiesById || {}) },
			nextDeletedDuties: { ...(dutyStore.deletedDuties || {}) },
			nextCurrentDayDutiesById: { ...(dutyStore.currentDayDutiesById || {}) },
			nextCurrentDayDriversById: { ...(userStore.currentDayDriversById || {}) },
			nextCurrentDayDuties: dutyStore.currentDayDuties ? { ...dutyStore.currentDayDuties, data: { ...dutyStore.currentDayDuties.data } } : null,
			nextCurrentDayDrivers: userStore.currentDayDrivers ? { ...userStore.currentDayDrivers, data: { ...userStore.currentDayDrivers.data } } : null,
			dutyLocationIndex: new Map(getDutyLocationIndex(dutyStore.dutiesMappedByDay || {})),
		};

		const getWorkingDuty = (eventId) => {
			return (
				ctx.nextMappedById[eventId] ||
				ctx.nextUnallocatedById[eventId] ||
				ctx.nextCurrentDayDutiesById[eventId] ||
				ctx.nextDeletedDuties[eventId] ||
				null
			);
		};

		try {
			for (const [dutyId, entry] of dutyEntries) {
				const data = { ...entry.data, id: dutyId };

				const wasMarkedDeleted = !!ctx.nextDeletedDuties[dutyId];
				const restoring = isRestoreOperation(data);

				const isExplicitDelete = entry.type === 'delete' || data.deleted === true;

				const isStickyDelete = !isExplicitDelete && wasMarkedDeleted && !restoring && entry.type !== 'create';
				const isHardDelete = isExplicitDelete || isStickyDelete;

				const isExplicitUnassignment = !isHardDelete && hasProperty(data, 'user') && (data.user === null || data.user === '');

				const snapshotDuty = getWorkingDuty(dutyId);
				const ownerCell = findOwnerInMappedByDay(ctx.dutyLocationIndex, dutyId);
				const originalUser = snapshotDuty?.user || ownerCell.user || data.user;
				const originalDay = snapshotDuty?.day || ownerCell.day || data.day;

				if (wasMarkedDeleted && restoring && !isHardDelete) {
					delete ctx.nextDeletedDuties[dutyId];
				}

				if (isStickyDelete) {
					const liveDuty = ctx.nextMappedById[dutyId] || ctx.nextUnallocatedById[dutyId] || ctx.nextCurrentDayDutiesById[dutyId];
					if (liveDuty?.user) {
						updateDriverUnassignment(ctx, liveDuty.user);
					}

					ctx.nextDeletedDuties[dutyId] = { ...snapshotDuty, ...ctx.nextDeletedDuties[dutyId], ...data };
					purgeStaleDutyReferences(ctx, dutyId);
				} else if (isHardDelete) {
					ctx.nextDeletedDuties[dutyId] = { ...snapshotDuty, ...ctx.nextDeletedDuties[dutyId], ...data };
					updateAllSourcesWithDuty(ctx, dutyId, null, originalUser, originalDay);
				} else if (isExplicitUnassignment) {
					const baseDuty = snapshotDuty || {};
					const updatedDuty = { ...baseDuty, ...data, user: null, day: originalDay };
					updateAllSourcesWithDuty(ctx, dutyId, updatedDuty, originalUser, originalDay);
				} else {
					const baseDuty = snapshotDuty || {};
					const targetUser = hasProperty(data, 'user') ? data.user : originalUser;
					const targetDay = data.day || originalDay;

					const updatedDuty = { ...baseDuty, ...data, user: targetUser, day: targetDay };
					updateAllSourcesWithDuty(ctx, dutyId, updatedDuty, originalUser, originalDay);
				}
			}

			const nextUnallocatedByDay = { ...(dutyStore.unallocatedDutiesMappedByDay || {}) };

			for (const dutyId of targetsToProcess) {
				const newDuty = ctx.nextUnallocatedById[dutyId];
				const newDay = newDuty?.day;

				for (const dayKey of Object.keys(nextUnallocatedByDay)) {
					if (dayKey === newDay) continue;
					const bucket = nextUnallocatedByDay[dayKey];
					if (!bucket || !bucket[dutyId]) continue;

					const rest = { ...bucket };
					delete rest[dutyId];

					if (Object.keys(rest).length === 0) {
						delete nextUnallocatedByDay[dayKey];
					} else {
						nextUnallocatedByDay[dayKey] = rest;
					}
				}

				if (newDuty && newDay) {
					nextUnallocatedByDay[newDay] = {
						...(nextUnallocatedByDay[newDay] || {}),
						[dutyId]: newDuty,
					};
				}
				ctx.dutyLocationIndex.delete(dutyId);
			}

			for (const dutyId of targetsToProcess) {
				dutyStore.invalidateCellState(dutyId);
			}

			// Single atomic Pinia commit
			dutyStore.$patch((state) => {
				state.dutiesMappedByDay = ctx.nextMappedByDay;
				state.dutiesMappedById = ctx.nextMappedById;
				state.unallocatedDutiesById = ctx.nextUnallocatedById;
				state.unallocatedDutiesMappedByDay = nextUnallocatedByDay;
				state.currentDayDutiesById = ctx.nextCurrentDayDutiesById;

				state.deletedDuties = ctx.nextDeletedDuties;
				if (ctx.nextCurrentDayDuties) {
					state.currentDayDuties = ctx.nextCurrentDayDuties;
				}
			});

			dutyStore.notifyMappedByDayChanged();

			userStore.$patch((state) => {
				state.currentDayDriversById = ctx.nextCurrentDayDriversById;

				if (ctx.nextCurrentDayDrivers) {
					state.currentDayDrivers = ctx.nextCurrentDayDrivers;
				}
			});

			cachedMappedByDayRaw = ctx.nextMappedByDay;
			cachedDutyLocationIndex = ctx.dutyLocationIndex;
		} catch (error) {
			console.error('[useSocketStore] flushDutyEvents failed — discarding this batch, state and cache unchanged:', error);
		}
	}

	async function socket_created_Day(payload) {
		const day = payload?.payload || payload;
		if (!day || !day.dateString) return;

		const formattedDay = {
			[day.dateString]: {
				data: day,
				isoDate: day.dateString,
				isHidden: false,
			},
		};

		dayStore.setDays(formattedDay);
	}

	async function socket_updated_Day(payload) {
		const day = payload?.payload || payload;
		if (day) dayStore.setDayStatus(day);
	}

	function socket_created_User(payload) {
		usePlanningStore().addDriver(payload.payload);
	}

	function socket_updated_User(payload) {
		usePlanningStore().updateDriver(payload.payload);
	}

	function socket_deleted_User(payload) {
		usePlanningStore().deleteDriver(payload.payload);
	}

	function socket_updated_UserGroup(payload) {
		try {
			const updatedGroup = payload?.payload;
			if (!updatedGroup?.id) {
				console.warn('[Socket UserGroup Update]: Invalid payload structure received', payload);
				return;
			}

			const selectedGroups = planningStore.filters?.selectedUserGroups?.selected;
			if (!Array.isArray(selectedGroups)) {
				console.warn('[Socket UserGroup Update]: selectedUserGroups.selected is not an array');
				return;
			}

			const hasMatch = selectedGroups.some((group) => group?.value === updatedGroup.id);
			if (!hasMatch) return;

			const users = Array.isArray(updatedGroup.users) ? updatedGroup.users : [];

			planningStore.filters.selectedUserGroups.selected = selectedGroups.map((group) => {
				if (group?.value === updatedGroup.id) {
					return {
						...group,
						children: users.map((user) => ({
							label: user?.displayName || user?.name || 'Unknown User',
							value: user?._id || user?.id || '',
						})),
					};
				}
				return group;
			});
		} catch (error) {
			console.error('[Socket UserGroup Update Error]:', error);
		}
	}

	function socket_deleted_UserGroup(payload) {
		try {
			const deletedGroupId = payload?.payload?.id || payload?.payload;
			if (!deletedGroupId || typeof deletedGroupId !== 'string') {
				console.warn('[Socket UserGroup Delete]: Invalid or missing group ID in payload', payload);
				return;
			}

			const selectedGroups = planningStore.filters?.selectedUserGroups?.selected;
			if (!Array.isArray(selectedGroups)) {
				console.warn('[Socket UserGroup Delete]: selectedUserGroups.selected is not an array');
				return;
			}

			const hasMatch = selectedGroups.some((group) => group?.value === deletedGroupId);

			if (hasMatch) {
				planningStore.filters.selectedUserGroups.selected = selectedGroups.filter((group) => group?.value !== deletedGroupId);
			}
		} catch (error) {
			console.error('[Socket UserGroup Delete Error]:', error);
		}
	}

	const debouncedCommitUpdateStatistics = debounce(() => {
		dutyStore.setUpdateStatistics();
	}, 500);

	async function socket_loadStatistics(payload) {
		debouncedCommitUpdateStatistics();
	}

	async function socket_updated_Preview(payload) {
		const data = payload.payload;

		if (Object.hasOwn(data, 'status') && (data.status === 'cancelled' || data.status === 'published')) {
			previewStore.clearPreview();
			return;
		}

		if (data.changes && previewStore.currentPreview) {
			const changesMappedByDay = Array.isArray(data.changes)
				? data.changes.reduce(
						(acc, item) => {
							if (item.day != null) {
								// Initialize the array if it doesn't exist, then push
								(acc[item.day] = acc[item.day] || []).push(item);
							}
							return acc;
						},
						{} as Record<string | number, Array<any>>
					)
				: {
						...(previewStore.changesMappedByDay ?? {}),
						...data.changes,
					};

			previewStore.currentPreview = {
				...previewStore.currentPreview,
				changes: data.changes,
			};

			previewStore.changesMappedByDay = changesMappedByDay;
		}

		if (data.violations && previewStore.currentPreview) {
			const violationsMappedByDay = Array.isArray(data.violations)
				? data.violations.reduce(
						(acc, item) => {
							if (item.day != null) {
								// Initialize the array if it doesn't exist, then push
								(acc[item.day] = acc[item.day] || []).push(item);
							}
							return acc;
						},
						{} as Record<string | number, Array<any>>
					)
				: {
						...(previewStore.previewViolationsMappedByDay ?? {}),
						...data.violations,
					};

			previewStore.currentPreview = {
				...previewStore.currentPreview,
				violations: data.violations,
			};

			previewStore.previewViolationsMappedByDay = violationsMappedByDay;
		}
		if (previewStore.currentPreview?._id === data.id) {
			previewStore.currentPreview = { ...previewStore.currentPreview, ...data };
		}
	}

	async function socket_created_Preview(payload: { payload: DutyChangePreviewResponse }) {
		const data = payload.payload;
		if (!data || data.status !== 'active') return;
		previewStore.setPreview(data);
	}

	async function setActiveViewContext(context) {
		activeViewContext.value = context;
	}

	async function socket_preview_alert_triggered(payload) {
		const data = payload.payload;
		const { messageKey, messageParams } = data;
		const message = t(messageKey, messageParams);
		const alertType = data.type === 'note' ? 'info' : data.type;
		alertStore.showAlert(message, alertType);
	}

	return {
		isConnected,
		activeViewContext,
		bindSocket,
		initializeSocket,
		socket_updated_Day,
		socket_deleted_Duty,
		socket_created_Duty,
		socket_updated_Duty,
		setActiveViewContext,
	};
});
